import React from 'react';
import { MenuItem, Select } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { createStyles, makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() =>
  createStyles({
    // One flex row that wraps AS A WHOLE when space is tight — the rows-per-page group drops to
    // its own line below — rather than letting either half wrap or overlap internally.
    pageOptions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      rowGap: 8,
      columnGap: 16,
      marginTop: 8,
      marginBottom: 40,
    },
    paginationWrapper: {
      display: 'flex',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: 0,
    },
    // MUI's Pagination wraps its own button list by default (flexWrap: 'wrap' on the ul) — this
    // is what actually caused the pagination itself to wrap; forcing nowrap here keeps every page
    // button on one line, no matter how the surrounding row wraps.
    paginationList: {
      flexWrap: 'nowrap',
    },
    rowsPerPageWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      whiteSpace: 'nowrap',
      flexShrink: 0,
      marginLeft: 'auto',
    },
    rowsPerPageLabel: {
      fontSize: '0.875rem',
    },
  }),
);

interface PaginationFooterProps {
  pageCount: number;
  /** 0-indexed, matching how every caller keeps its own page state. */
  page: number;
  onPageChange: (newPage: number) => void;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  onRowsPerPageChange: (value: number) => void;
  rowsPerPageLabel?: string;
  style?: React.CSSProperties;
}

/**
 * Shared pagination + rows-per-page footer for the My Work table and Gantt views. Pulled out
 * once both needed the identical fix — MUI's Pagination wraps its button list by default, and the
 * two halves need to never overlap — so the layout only has to get this right in one place.
 */
export default function PaginationFooter({
  pageCount,
  page,
  onPageChange,
  rowsPerPage,
  rowsPerPageOptions,
  onRowsPerPageChange,
  rowsPerPageLabel = 'Rows per page',
  style,
}: PaginationFooterProps) {
  const classes = useStyles();

  return (
    <div className={classes.pageOptions} style={style}>
      <div className={classes.paginationWrapper}>
        {pageCount > 1 && (
          <Pagination
            count={pageCount}
            shape="rounded"
            color="primary"
            showFirstButton
            showLastButton
            page={page + 1}
            onChange={(_event, value) => onPageChange(value - 1)}
            classes={{ ul: classes.paginationList }}
          />
        )}
      </div>
      <div className={classes.rowsPerPageWrapper}>
        <span className={classes.rowsPerPageLabel}>{rowsPerPageLabel}</span>
        <Select
          value={rowsPerPage}
          onChange={(event) => onRowsPerPageChange(event.target.value as number)}
          style={{ background: 'transparent' }}
          SelectDisplayProps={{ style: { background: 'transparent' } }}
        >
          {rowsPerPageOptions.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );
}
